import { useMutation } from "@apollo/client";
import { parseError } from "apollo";
import { AppContext } from "App";
import { ORIGIN_COUNTRIES } from "AppSettings";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { FileSelector, Input, Select } from "components/base/Form";
import Page from "components/base/Page";
import { Alert } from "components/base/Toast";
import MultiProductSelector from "components/shared/MultiProductSelector";
import useFileUploader from "hooks/useFileUploader";
import { useModals } from "ModalProvider";
import moment from "moment";
import { useContext, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EDIT_CERTIFICATE } from "utils/permissions";
import { getOriginalFileName } from "utils/string";
import ComplianceStandardsSelector from "./ComplianceStandardsSelector";
import { TEST_REPORT_ISSUERS } from "./consts";
import {
  CREATE_LAB_TEST_REPORT,
  DELETE_LAB_TEST_REPORT,
  FETCH_LAB_TEST_REPORTS,
  UPDATE_LAB_TEST_REPORT,
} from "./graphql";

const DEFAULT_FORM = {
  title: "",
  country: "CN",
  issuer: "",
  issuedDate: moment().format("YYYY-MM-DD"),
  reportNumber: "",
  validityMonths: "24",
  validUntil: "",
};

const LabTestReportFormPage = ({
  mode = "create",
  id = null,
  initialValues = {},
  initialProducts = [],
  initialStandards = [],
  initialFiles = [],
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const modal = useModals();
  const isEditMode = mode === "edit" && !!id;
  const { hasPermission } = useContext(AppContext);
  const canEditLabTestReport = hasPermission(EDIT_CERTIFICATE);

  const [form, setForm] = useState({ ...DEFAULT_FORM, ...initialValues });
  const [selectedProducts, setSelectedProducts] = useState(initialProducts);
  const [selectedStandards, setSelectedStandards] = useState(initialStandards);
  const [files, setFiles] = useState(initialFiles);
  const issuerKeys = Object.keys(TEST_REPORT_ISSUERS);

  const { isUploading, handleFileUpload } = useFileUploader(
    handleUploadedFiles,
    "AWS",
  );

  const [createLabTestReport, createLabTestReportRes] = useMutation(
    CREATE_LAB_TEST_REPORT,
    {
      onCompleted(res) {
        Alert("success", "Lab test report created.");
        const newId = res?.createLabTestReport?.labTestReport?.id;
        if (newId) {
          navigate(`/test-reports/${newId}`);
        }
      },
      onError(error) {
        Alert("error", parseError(error));
      },
      refetchQueries: [
        { query: FETCH_LAB_TEST_REPORTS, variables: { offset: 0 } },
      ],
      awaitRefetchQueries: true,
    },
  );
  const [updateLabTestReport, updateLabTestReportRes] = useMutation(
    UPDATE_LAB_TEST_REPORT,
    {
      onCompleted() {
        Alert("success", "Lab test report updated.");
      },
      onError(error) {
        Alert("error", parseError(error));
      },
      refetchQueries: [
        { query: FETCH_LAB_TEST_REPORTS, variables: { offset: 0 } },
      ],
      awaitRefetchQueries: true,
    },
  );

  const [deleteLabTestReport, deleteLabTestReportRes] = useMutation(
    DELETE_LAB_TEST_REPORT,
    {
      onCompleted() {
        Alert("success", "Lab test report deleted.");

        navigate(`/test-reports?${searchParams.toString()}`);
      },
      onError(error) {
        Alert("error", parseError(error));
      },
      refetchQueries: [
        { query: FETCH_LAB_TEST_REPORTS, variables: { offset: 0 } },
      ],
      awaitRefetchQueries: true,
    },
  );

  function onChange(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function removeProduct(productId) {
    setSelectedProducts((prev) => prev.filter((p) => p.id !== productId));
  }

  function removeStandard(standardId) {
    setSelectedStandards((prev) => prev.filter((s) => s.id !== standardId));
  }

  function handleUploadedFiles(urls) {
    setFiles((prev) => [...prev, ...urls]);
  }

  function removeFile(index) {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }

  function chooseProductHandler() {
    modal.present({
      title: "Choose Products",
      center: true,
      children: (
        <MultiProductSelector
          defaultSelectedProducts={selectedProducts}
          onDone={didChooseProductsHandler}
        />
      ),
    });
  }

  function didChooseProductsHandler(products) {
    modal.hide();
    setSelectedProducts(products);
  }

  function chooseStandardHandler() {
    modal.present({
      title: "Choose Standards",
      center: true,
      children: (
        <ComplianceStandardsSelector
          defaultSelectedStandards={selectedStandards}
          onDone={didChooseStandardsHandler}
        />
      ),
    });
  }

  function didChooseStandardsHandler(standards) {
    modal.hide();
    setSelectedStandards(standards);
  }

  function buildLabTestReportVariables(title) {
    const validityMonths = Number(form.validityMonths);
    const fileUrls = files.filter(Boolean);

    return {
      title,
      country: form.country || undefined,
      issuer: form.issuer.trim() || undefined,
      issuedDate: form.issuedDate,
      reportNumber: form.reportNumber.trim() || undefined,
      validityMonths:
        form.validityMonths === "" || Number.isNaN(validityMonths)
          ? undefined
          : validityMonths,
      validUntil: form.validUntil || undefined,
      productIds: selectedProducts.map((p) => p.id),
      standardIds: selectedStandards.map((standard) => standard.id),
      fileUrls: fileUrls.length > 0 ? fileUrls : undefined,
    };
  }

  function saveHandler() {
    const title = form.title.trim();
    if (!title) {
      Alert("error", "Please enter title.");
      return;
    }
    if (!form.issuedDate) {
      Alert("error", "Please select issued date.");
      return;
    }
    if (isUploading) {
      Alert("error", "Please wait for file upload to complete.");
      return;
    }

    const variables = buildLabTestReportVariables(title);

    if (isEditMode) {
      updateLabTestReport({ variables: { id, ...variables } });
      return;
    }
    createLabTestReport({ variables });
  }

  function duplicateHandler() {
    if (!isEditMode) return;
    const shouldDuplicate = window.confirm(
      "Are you sure you want to duplicate this lab test report?",
    );
    if (!shouldDuplicate) return;

    const title = form.title.trim();
    if (!title) {
      Alert("error", "Please enter title.");
      return;
    }
    if (!form.issuedDate) {
      Alert("error", "Please select issued date.");
      return;
    }
    if (isUploading) {
      Alert("error", "Please wait for file upload to complete.");
      return;
    }

    createLabTestReport({
      variables: buildLabTestReportVariables(`Duplicated of ${title}`),
    });
  }

  function deleteHandler() {
    if (!isEditMode) return;
    const shouldDelete = window.confirm(
      "Are you sure you want to delete this lab test report?",
    );
    if (!shouldDelete) return;
    deleteLabTestReport({ variables: { id } });
  }

  if (!canEditLabTestReport) {
    return (
      <Page
        title={isEditMode ? "Edit Lab Test Report" : "New Lab Test Report"}
        backTo="./.."
      >
        <div className="p-6">
          <div className="card text-sm text-orange-600">
            You don&apos;t have permission to edit lab test reports.
          </div>
        </div>
      </Page>
    );
  }

  return (
    <Page
      title={isEditMode ? "Edit Lab Test Report" : "New Lab Test Report"}
      backTo="./.."
      rightButtons={
        <div className="flex items-center space-x-4">
          {isEditMode && <Button onClick={duplicateHandler}>Duplicate</Button>}

          <Button
            primary
            loading={
              createLabTestReportRes.loading || updateLabTestReportRes.loading
            }
            onClick={saveHandler}
          >
            {isEditMode ? "Save Report" : "Create Report"}
          </Button>
        </div>
      }
    >
      <div className="p-6 grid grid-cols-12 gap-6">
        <div className="col-span-8 space-y-6">
          {createLabTestReportRes.error ||
          updateLabTestReportRes.error ||
          deleteLabTestReportRes.error ? (
            <div className="card">
              <Errors
                error={
                  createLabTestReportRes.error ||
                  updateLabTestReportRes.error ||
                  deleteLabTestReportRes.error
                }
              />
            </div>
          ) : null}

          <div className="card space-y-4">
            <h5>Report Information</h5>
            <div className="-mx-2">
              <table>
                <tbody>
                  <tr>
                    <td>
                      <label>Country</label>
                    </td>
                    <td>
                      <Select
                        className="w-full"
                        value={form.country}
                        onChange={(e) => onChange("country", e.target.value)}
                      >
                        {ORIGIN_COUNTRIES.map((item) => (
                          <option key={item.code} value={item.code}>
                            {item.name} {item.emoji}
                          </option>
                        ))}
                      </Select>
                    </td>
                  </tr>
                  <tr>
                    <td className="w-44">
                      <label>Title *</label>
                    </td>
                    <td>
                      <Input
                        className="w-full"
                        value={form.title}
                        onChange={(e) => onChange("title", e.target.value)}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label>Report Number</label>
                    </td>
                    <td>
                      <Input
                        className="w-full"
                        value={form.reportNumber}
                        onChange={(e) =>
                          onChange("reportNumber", e.target.value)
                        }
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>
                      <label>Issuer</label>
                    </td>
                    <td>
                      <Select
                        className="w-full"
                        value={form.issuer}
                        onChange={(e) => onChange("issuer", e.target.value)}
                      >
                        <option value="">-</option>
                        {issuerKeys.map((issuer) => (
                          <option key={issuer} value={issuer}>
                            {issuer}
                          </option>
                        ))}
                      </Select>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label>Issued Date *</label>
                    </td>
                    <td>
                      <Input
                        className="w-full"
                        type="date"
                        value={form.issuedDate}
                        onChange={(e) => onChange("issuedDate", e.target.value)}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="align-baseline">
                      <label>Valid Until</label>
                    </td>
                    <td className="align-baseline">
                      <Input
                        className="w-full"
                        type="date"
                        value={form.validUntil}
                        onChange={(e) => onChange("validUntil", e.target.value)}
                      />
                      <div className="opacity-70 text-xs p-1">
                        Default Valid Until will be calculated based on Issued
                        Date and {form.validityMonths} Months if Valid Until is
                        not provided.
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="card space-y-4">
            <h5>Standards</h5>
            {selectedStandards.length > 0 ? (
              <div className="-mx-2">
                <table className="with-line">
                  <tbody>
                    {selectedStandards.map((standard) => (
                      <tr key={standard.id}>
                        <td>
                          <div className="flex justify-between items-center">
                            <div>{standard.name || "-"}</div>
                            <Button
                              trash
                              onClick={() => removeStandard(standard.id)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            <div>
              <Button onClick={chooseStandardHandler}>
                + Choose Standards
              </Button>
            </div>
          </div>

          <div className="card space-y-4">
            <h5>Report Files</h5>

            {files.length > 0 ? (
              <div className="space-y-2">
                {files.map((url, index) => (
                  <div
                    key={`${url}-${index}`}
                    className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700 py-2"
                  >
                    <div className="flex-1">
                      <a
                        href={url}
                        className="link"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {getOriginalFileName(url)}
                      </a>
                    </div>
                    <Button trash onClick={() => removeFile(index)} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm opacity-60">No files uploaded.</div>
            )}

            <FileSelector
              title="+ Add Files"
              uploading={isUploading}
              onChange={(e) => {
                handleFileUpload([...e.target.files]);
                e.target.value = null;
              }}
            />
          </div>
        </div>

        <div className="col-span-4">
          <div className="space-y-6">
            <div className="card space-y-4">
              <h5>Products</h5>
              {selectedProducts.length > 0 ? (
                <div className="-mx-2">
                  <table className="with-line">
                    <tbody>
                      {selectedProducts.map((product) => (
                        <tr key={product.id}>
                          <td>
                            <div className="flex justify-between items-center">
                              <div>
                                [{product.number}] {product.name}
                              </div>
                              <Button
                                trash
                                onClick={() => removeProduct(product.id)}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}

              <div>
                <Button onClick={chooseProductHandler}>
                  + Choose Products
                </Button>
              </div>
            </div>

            <div className="flex justify-end">
              {isEditMode ? (
                <Button
                  danger
                  loading={deleteLabTestReportRes.loading}
                  onClick={deleteHandler}
                >
                  Delete Lab Test Report
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
};

export default LabTestReportFormPage;
